import axios from 'axios';
import memoize from 'memoizee';
import config from './config';
import { ILocation } from '../store/reducers/locations';
import { processError } from '../helpers/logger';

const marker = 'selection_frontend_locations';

export interface IGetLocationsParams {
    ids?: Array<string | number>;
    route?: string;
    limit?: number;
}

/** мемоизированный метод */
export const getLocationByRoute = memoize(
    (route: string) => {
        return getLocationsByParams({ route });
    },
    { promise: true, maxAge: 1000 * 60 * 10, preFetch: true, max: 30 },
);

export const getLocationsByParams = async (params: IGetLocationsParams): Promise<Array<ILocation>> => {
    const { ids, route, limit = 10 } = params;
    const url = `${config.LOCATIONS}/v1.0/locations`;

    try {
        const { data } = await axios.get<ILocation[]>(url, {
            params: {
                ...(Array.isArray(ids) ? { ids: ids.join(',') } : {}),
                ...(route ? { route } : {}),
                limit,
            },
        });
        return data;
    } catch (e) {
        processError(e, { url, marker });
        return [];
    }
};

export interface ILocationsParams {
    query: string;
    limit?: number;
    includeVillages?: boolean;
}

export const suggest = async ({
    query,
    limit = 5,
    includeVillages = false,
}: ILocationsParams): Promise<Array<ILocation>> => {
    const url = `${config.LOCATIONS}/v1.0/suggest/`;
    try {
        const { data } = await axios.get<Array<ILocation>>(url, {
            params: {
                q: query,
                limit,
                includeVillages,
            },
        });
        return data;
    } catch (e) {
        processError(e, { url, marker });
        return [];
    }
};

export const getLocationByAddress = async (address: string): Promise<ILocation | null> => {
    const url = `${config.LOCATIONS}/v1.0/locations/settlement`;
    try {
        const { data } = await axios.get<ILocation | null>(url, { params: { address } });
        return data;
    } catch (e) {
        processError(e, { url, marker });
        return null;
    }
};

export const getLocationRegionByAddress = async (address: string): Promise<ILocation | null> => {
    const url = `${config.LOCATIONS}/v1.0/locations/region`;
    try {
        const { data } = await axios.get<ILocation | null>(url, { params: { address } });
        return data;
    } catch (e) {
        processError(e, { url, marker });
        return null;
    }
};

export const loadRegions = async (): Promise<ILocation[]> => {
    const url = `${config.LOCATIONS}/v1/locations/regions`;
    try {
        const { data } = await axios.get<ILocation[]>(url, {
            timeout: 5000,
        });
        return data;
    } catch (e) {
        processError(e, { url, marker });
        return [];
    }
};

export const loadRegionalCenters = async (): Promise<ILocation[]> => {
    const url = `${config.LOCATIONS}/v1/locations/regional-centers`;
    try {
        const { data } = await axios.get<ILocation[]>(url, {
            timeout: 5000,
        });
        return data;
    } catch (e) {
        processError(e, { url, marker });
        return [];
    }
};

export const byAlias = async (alias: string): Promise<ILocation | null> => {
    if (!alias) return null;
    const url = `${config.LOCATIONS}/v1/locations/by-alias/${alias}`;
    try {
        const { data } = await axios.get<ILocation>(url);
        return data;
    } catch (e) {
        processError(e, { url, marker });
        return null;
    }
};
